import { createFileRoute, Link, notFound, redirect, type ErrorComponentProps } from '@tanstack/react-router'
import { z } from 'zod'
import { ReservationForm } from '@/features/accommodation/reservation/components/ReservationForm'
import { accommodationDetailQueryOptions, enumsQueryOptions } from '@/api/accommodation/hooks'
import { Button } from '@/components/ui/button'
import { HttpError } from '@/api/http'
import { SITE_NAME, noindexMeta } from '@/lib/seo/siteMeta'

const reservationParamsSchema = z.object({
  slug: z.string().min(1),
  unitSlug: z.string().min(1),
})

// Missing or unparsable dates must not throw — a crawler hitting the bare URL
// would get an SSR 500. They stay undefined and `beforeLoad` sends the visitor
// back to the detail page to pick a term.
const reservationSearchSchema = z.object({
  dateFrom: z.coerce.date().optional().catch(undefined),
  dateTo: z.coerce.date().optional().catch(undefined),
})

function ReservationErrorComponent({ error }: ErrorComponentProps) {
  let title = 'Chyba při načítání'
  let message = 'Nepodařilo se načíst stránku rezervace. Zkuste to prosím znovu.'

  if (error instanceof HttpError && error.status === 404) {
    title = 'Ubytování nenalezeno'
    message = 'Toto ubytování neexistuje nebo bylo odstraněno.'
  }

  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">{title}</h1>
      <p className="mb-6 max-w-sm text-slate-500">{message}</p>
      <Button asChild>
        <Link to="/ubytovani/hledat">Zpět na vyhledávání</Link>
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_layout/ubytovani/$slug/$unitSlug/rezervace')({
  component: ReservationPage,
  errorComponent: ReservationErrorComponent,
  params: {
    parse: (params) => reservationParamsSchema.parse(params),
    stringify: (params) => ({ slug: params.slug, unitSlug: params.unitSlug }),
  },
  validateSearch: reservationSearchSchema,
  beforeLoad: ({ params, search }) => {
    if (!search.dateFrom || !search.dateTo) {
      throw redirect({
        to: '/ubytovani/$slug/$unitSlug',
        params: { slug: params.slug, unitSlug: params.unitSlug },
      })
    }
  },
  loader: async ({ context, params }) => {
    const [accommodation] = await Promise.all([
      context.queryClient.query({
        ...accommodationDetailQueryOptions(params.slug, params.unitSlug),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...enumsQueryOptions(),
        staleTime: 'static',
      }),
    ]).catch((error: unknown) => {
      // An unknown slug must surface as a real 404, not an SSR 500.
      if (error instanceof HttpError && error.status === 404) throw notFound()
      throw error
    })
    // A paused or not yet approved unit is a preview only, nothing to book.
    if (!accommodation.unit.sellable) {
      throw redirect({
        to: '/ubytovani/$slug/$unitSlug',
        params: { slug: params.slug, unitSlug: params.unitSlug },
      })
    }
  },
  head: () => ({
    meta: [{ title: `Rezervace | ${SITE_NAME}` }, ...noindexMeta()],
  }),
})

function ReservationPage() {
  const { slug, unitSlug } = Route.useParams()
  const { dateFrom, dateTo } = Route.useSearch()

  // `beforeLoad` redirects away when either date is missing, so this only
  // narrows the type.
  if (!dateFrom || !dateTo) return null

  return <ReservationForm slug={slug} unitSlug={unitSlug} dateFrom={dateFrom} dateTo={dateTo} />
}
